import React, { useState } from 'react';
import { ArrowLeftRight, Copy, Trash2 } from 'lucide-react';

const JsonToUrlParamsTool = () => {
  const [json, setJson] = useState('');
  const [urlParams, setUrlParams] = useState('');
  const [mode, setMode] = useState('toParams'); // toParams, toJson
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(false);

  const convertToParams = () => {
    setError('');
    setUrlParams('');

    if (!json.trim()) {
      setError('Please enter JSON data');
      return;
    }

    try {
      const obj = JSON.parse(json);
      
      const params = new URLSearchParams();
      
      const flatten = (data, prefix = '') => {
        if (Array.isArray(data)) {
          data.forEach((item, index) => {
            flatten(item, `${prefix}[${index}]`);
          });
        } else if (typeof data === 'object' && data !== null) {
          Object.entries(data).forEach(([key, value]) => {
            const newKey = prefix ? `${prefix}[${key}]` : key;
            flatten(value, newKey);
          });
        } else {
          params.append(prefix, data === null ? '' : String(data));
        }
      };
      
      flatten(obj);
      setUrlParams(params.toString());
    } catch (e) {
      setError('Invalid JSON: ' + e.message);
    }
  };

  const convertToJson = () => {
    setError('');
    setJson('');

    if (!urlParams.trim()) {
      setError('Please enter URL parameters');
      return;
    }

    try {
      // Handle leading ? if present
      const cleanParams = urlParams.startsWith('?') ? urlParams.slice(1) : urlParams;
      const params = new URLSearchParams(cleanParams);
      const result = {};

      params.forEach((value, key) => {
        // Handle nested keys like user[name] or items[0]
        const keys = key.replace(/\]/g, '').split('[');
        let current = result;
        
        for (let i = 0; i < keys.length - 1; i++) {
          const k = keys[i];
          const nextKey = keys[i + 1];
          const isNextArray = /^\d+$/.test(nextKey);
          
          if (!(k in current)) {
            current[k] = isNextArray ? [] : {};
          }
          current = current[k];
        }
        
        const finalKey = keys[keys.length - 1];
        current[finalKey] = value;
      });

      setJson(JSON.stringify(result, null, 2));
    } catch (e) {
      setError('Error parsing parameters: ' + e.message);
    }
  };

  const copyOutput = () => {
    const output = mode === 'toParams' ? urlParams : json;
    navigator.clipboard.writeText(output);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const loadSample = () => {
    if (mode === 'toParams') {
      setJson(JSON.stringify({
        name: "John Doe",
        email: "john@example.com",
        age: 30,
        active: true,
        tags: ["developer", "designer"],
        address: {
          city: "New York",
          country: "USA"
        }
      }, null, 2));
    } else {
      setUrlParams('name=John%20Doe&email=john%40example.com&age=30&active=true&tags[0]=developer&tags[1]=designer&address[city]=New%20York&address[country]=USA');
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex gap-2">
        <button
          onClick={() => setMode('toParams')}
          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
            mode === 'toParams' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          JSON → URL Params
        </button>
        <button
          onClick={() => setMode('toJson')}
          className={`flex-1 px-4 py-2 rounded-lg font-medium transition-colors ${
            mode === 'toJson' ? 'bg-blue-600 text-white' : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
          }`}
        >
          URL Params → JSON
        </button>
      </div>

      {mode === 'toParams' ? (
        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">JSON Input</label>
              <button onClick={loadSample} className="text-xs text-blue-400 hover:text-blue-300">Load Sample</button>
            </div>
            <textarea
              value={json}
              onChange={(e) => setJson(e.target.value)}
              placeholder='{"key": "value"}'
              className="w-full h-48 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={convertToParams}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            <ArrowLeftRight size={18} />
            Convert to URL Params
          </button>
          {urlParams && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">URL Parameters Output</label>
              <textarea
                value={urlParams}
                readOnly
                className="w-full h-32 bg-gray-800 border border-gray-700 rounded-lg p-4 text-green-400 font-mono text-sm"
              />
            </div>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          <div>
            <div className="flex justify-between items-center mb-2">
              <label className="text-sm font-medium text-gray-300">URL Parameters Input</label>
              <button onClick={loadSample} className="text-xs text-blue-400 hover:text-blue-300">Load Sample</button>
            </div>
            <textarea
              value={urlParams}
              onChange={(e) => setUrlParams(e.target.value)}
              placeholder="name=John&email=john@example.com"
              className="w-full h-32 bg-gray-800 border border-gray-700 rounded-lg p-4 text-white font-mono text-sm placeholder-gray-500 focus:border-blue-500"
            />
          </div>
          <button
            onClick={convertToJson}
            className="flex items-center gap-2 px-6 py-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg transition-colors font-medium"
          >
            <ArrowLeftRight size={18} />
            Convert to JSON
          </button>
          {json && (
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">JSON Output</label>
              <pre className="w-full bg-gray-800 border border-gray-700 rounded-lg p-4 text-green-400 font-mono text-sm overflow-auto max-h-64">
                {json}
              </pre>
            </div>
          )}
        </div>
      )}

      {error && (
        <div className="p-3 bg-red-900/30 border border-red-700 rounded-lg text-red-400 text-sm">
          {error}
        </div>
      )}

      <div className="flex gap-3">
        <button
          onClick={copyOutput}
          disabled={mode === 'toParams' ? !urlParams : !json}
          className="flex items-center gap-2 px-4 py-2 bg-green-600 hover:bg-green-700 disabled:bg-gray-700 disabled:cursor-not-allowed text-white rounded-lg transition-colors"
        >
          <Copy size={16} />
          {copied ? 'Copied!' : 'Copy Output'}
        </button>
        <button
          onClick={() => { setJson(''); setUrlParams(''); setError(''); }}
          className="flex items-center gap-2 px-4 py-2 bg-gray-700 hover:bg-gray-600 text-white rounded-lg transition-colors"
        >
          <Trash2 size={16} />
          Clear
        </button>
      </div>
    </div>
  );
};

export default JsonToUrlParamsTool;
